import { makeObservable, observable, action, computed, runInAction } from "mobx"
import { BasicPageantDTO, BasicPageantDTOPagedResult, EventBaseModel, EventCursor, EventModel, EventPersonnelModel, PersonnelType, TemporalFilter } from "API/Generated/data-contracts"
import { Event } from "API/Generated/Event"
import { makePersistable } from "mobx-persist-store";
import { RequestStatus } from "SessionState/RequestHandling/RequestStatus";
import { compressData, decompressData } from 'utilities/compression'; // Adjust the path as needed
import { EventPersonnel } from "API/Generated/EventPersonnel";



interface Request {
  id: number;
  status: RequestStatus;
}

interface EventResponse {
  data?: {
    data?: EventModel[] | null;
  };
}

interface PersonnelEventsResponse {
  upcomingEvents: EventModel[];
  pastEvents: EventModel[];
}




class EventStore {
  EventService: Event = new Event();
  EventPersonnelService: EventPersonnel = new EventPersonnel();
  CurrentEvent: EventModel;
  DirectorEventList: EventModel[];
  FutureEventList: EventModel[];
  PastEventList: EventModel[];
  FutureEventListBasicInfo: EventBaseModel[];
  personnelTypes: PersonnelType[] = [];
  totalFutureEventsCount: number = 0;
  eventPublicDetails: EventModel | null = null;
  loading: boolean = false;
  error: string | null = null;
 
  CurrentEventRequest: Request = {
    id: 0,
    status: RequestStatus.PENDING
  };

  FutureEventsListRequest: Request = {
    id: 0,
    status: RequestStatus.PENDING
  };

  PastEventsListRequest: Request = {
    id: 0,
    status: RequestStatus.PENDING
  };

  //New Dynamic Search for basic event cards - testing

  dynamicItems: BasicPageantDTO[] = [];
dynamicCursor: EventCursor | null = null;
dynamicHasMore = true;
dynamicIsLoading = false;
dynamicTotal: number | undefined = undefined;

// Last filters used to fetch (so we can detect changes and reset)
dynamicLastFilters:
  | {
      when: TemporalFilter;
      limit?: number;
      includeMetadata?: boolean;
      search?: string | null;
      stateId?: number | null;
      pageantId?: number | null;
      acceptingEntries?: boolean | null;
    }
  | null = null;

  // in EventStore
hydrateDynamicSnapshot(snap: {
  items: BasicPageantDTO[];
  cursor: EventCursor | null;
  hasMore: boolean;
  total: number | null;
}) {
  runInAction(() => {
    (this as any).dynamicItems = snap.items;
    (this as any).dynamicCursor = snap.cursor;
    (this as any).dynamicHasMore = snap.hasMore;
    (this as any).dynamicTotal = snap.total;
    (this as any).dynamicIsLoading = false;
  });
}



  constructor(currentEvent: EventModel, directorEventList: EventModel[], futureEventList: EventModel[], pastEventList: EventModel[], futureEventsListBasicInfo: EventBaseModel[], personnelTypes: PersonnelType[]) {
    makeObservable(this, {
      CurrentEvent: observable,
      DirectorEventList: observable,
      FutureEventList: observable,
      FutureEventListBasicInfo: observable,
      PastEventList: observable,
      personnelTypes: observable,
      getEvent: action,
      saveEvent: action,
      getEventByDirector: action,
      getFutureEvents: action,
      getFutureEventsSearchFilter: action,
      fetchTotalFutureEventsCount: action,
      getPastEvents: action,
      getAllEvents: action,
      resetCurrentEvent: action,
      addJudgeToEvent: action,
      getJudgesByEventId: action,

        // ...existing...
  dynamicItems: observable,
  dynamicCursor: observable,
  dynamicHasMore: observable,
  dynamicIsLoading: observable,
  dynamicTotal: observable,
  dynamicLastFilters: observable,

  resetDynamic: action,
  getBasicEventsDynamicFirstPage: action,
  getBasicEventsDynamicNextPage: action,
    })
    // makePersistable(this, { name: 'EventStore', properties: ['CurrentEvent', 'DirectorEventList', 'FutureEventList', 'PastEventList', 'FutureEventListBasicInfo'] });
    this.CurrentEvent = currentEvent;
    this.DirectorEventList = directorEventList;
    this.FutureEventList = futureEventList;
    this.FutureEventListBasicInfo = futureEventsListBasicInfo;
    this.PastEventList = pastEventList;
  }

  //==============Dynamic search for basic events - testing===========
  resetDynamic() {
  this.dynamicItems = [];
  this.dynamicCursor = null;
  this.dynamicHasMore = true;
  this.dynamicIsLoading = false;
  this.dynamicTotal = undefined;
}


async fetchBasicEventsDynamic(
  when: TemporalFilter,
  limit: number,
  opts?: {
    cursor?: Partial<EventCursor>; // both fields must be present together
    includeMetadata?: boolean;
    search?: string | null;
    stateId?: number | null;
    pageantId?: number | null;
    acceptingEntries?: boolean | null;
    signal?: AbortSignal;
  }
): Promise<BasicPageantDTOPagedResult> {
  const qs = new URLSearchParams();

  // NOTE: If your server expects the enum *name* instead of number, map it:
  // const whenParam = typeof when === 'number' ? (TemporalFilter as any)[when] ?? when : when;
  // qs.set("when", String(whenParam));
  qs.set("when", String(when)); // works if server accepts numeric enums (Swagger often does)

  qs.set("limit", String(Math.max(1, Math.min(500, limit))));

  // cursor must be all-or-nothing
  const hasDate = !!opts?.cursor?.sortDateUtc;
  const hasId = opts?.cursor?.sortEventId != null;
  if (hasDate !== hasId) {
    throw new Error("cursorDateUtc and cursorEventId must be provided together, or both omitted.");
  }
  if (hasDate && hasId) {
    // Ensure ISO UTC; if you already have ISO, keep as-is
    const isoUtc =
      /\dZ$/.test(String(opts!.cursor!.sortDateUtc))
        ? String(opts!.cursor!.sortDateUtc)
        : new Date(String(opts!.cursor!.sortDateUtc)).toISOString();

    qs.set("cursorDateUtc", isoUtc);
    qs.set("cursorEventId", String(opts!.cursor!.sortEventId));
  }

  if (opts?.search) qs.set("search", opts.search);
  if (opts?.stateId != null) qs.set("stateId", String(opts.stateId));
  if (opts?.pageantId != null) qs.set("pageantId", String(opts.pageantId));
  if (opts?.acceptingEntries != null) qs.set("acceptingEntries", String(opts.acceptingEntries));
  if (opts?.includeMetadata) qs.set("includeMetadata", "true");

  const res = await fetch(`/Event/GetBasicEventsDynamic?${qs.toString()}`, {
    signal: opts?.signal,
    method: "GET",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
  return (await res.json()) as BasicPageantDTOPagedResult;
}

async getBasicEventsDynamicFirstPage(params: {
  when: TemporalFilter;
  limit?: number;
  includeMetadata?: boolean;
  search?: string | null;
  stateId?: number | null;
  pageantId?: number | null;
  acceptingEntries?: boolean | null;
  signal?: AbortSignal;
}) {
  // Detect filter changes (ignore limit for equality)
  const nextFilters = {
    when: params.when,
    includeMetadata: params.includeMetadata,
    search: params.search ?? null,
    stateId: params.stateId ?? null,
    pageantId: params.pageantId ?? null,
    acceptingEntries: params.acceptingEntries ?? null,
    limit: params.limit ?? 50,
  };
  const changed =
    JSON.stringify({ ...nextFilters, limit: undefined }) !==
    JSON.stringify({ ...(this.dynamicLastFilters ?? {}), limit: undefined });

  if (changed) {
    this.resetDynamic();
    this.dynamicLastFilters = { ...nextFilters };
  }
  if (!this.dynamicHasMore || this.dynamicIsLoading) return;

  this.dynamicIsLoading = true;
  try {
    const page = await this.fetchBasicEventsDynamic(nextFilters.when, nextFilters.limit!, {
      includeMetadata: nextFilters.includeMetadata,
      search: nextFilters.search,
      stateId: nextFilters.stateId ?? undefined,
      pageantId: nextFilters.pageantId ?? undefined,
      acceptingEntries: nextFilters.acceptingEntries ?? undefined,
      signal: params.signal,
    });

    runInAction(() => {
      const items = page.items ?? [];
      this.dynamicItems = items;
      this.dynamicCursor = (page as any).nextCursor ?? null;
      this.dynamicHasMore = Boolean((page as any).nextCursor);
      this.dynamicTotal = (page as any).total ?? this.dynamicTotal;
    });
  } finally {
    runInAction(() => {
      this.dynamicIsLoading = false;
    });
  }
}

async getBasicEventsDynamicNextPage(signal?: AbortSignal) {
  if (!this.dynamicHasMore || this.dynamicIsLoading || !this.dynamicLastFilters) return;

  this.dynamicIsLoading = true;
  try {
    const page = await this.fetchBasicEventsDynamic(this.dynamicLastFilters.when, this.dynamicLastFilters.limit ?? 50, {
      includeMetadata: this.dynamicLastFilters.includeMetadata,
      search: this.dynamicLastFilters.search ?? undefined,
      stateId: this.dynamicLastFilters.stateId ?? undefined,
      pageantId: this.dynamicLastFilters.pageantId ?? undefined,
      acceptingEntries: this.dynamicLastFilters.acceptingEntries ?? undefined,
      cursor: this.dynamicCursor ?? undefined,
      signal,
    });

    runInAction(() => {
      const existing = new Set(this.dynamicItems.map(e => e.eventId));
      const incoming = (page.items ?? []).filter(e => !existing.has(e.eventId));
      this.dynamicItems = this.dynamicItems.concat(incoming);
      this.dynamicCursor = (page as any).nextCursor ?? null;
      this.dynamicHasMore = Boolean((page as any).nextCursor);
      if ((page as any).total != null) this.dynamicTotal = (page as any).total;
    });
  } finally {
    runInAction(() => {
      this.dynamicIsLoading = false;
    });
  }
}



  //==============END - Dynamic search for basic events - testing===========


  async addJudgeToEvent(eventPersonnel: EventPersonnelModel) {
    try {
      await this.EventPersonnelService.addJudgeToEventCreate(eventPersonnel);
      runInAction(() => {
        this.CurrentEvent?.personnel?.push(eventPersonnel); // Update the local state
      });
    } catch (error) {
      console.error("Error adding judge to event:", error);
    }
  }

  async getJudgesByEventId(eventId: number): Promise<EventPersonnelModel[]> {
    try {
      const result = await this.EventPersonnelService.getJudgesByEventDetail(eventId);
      return result?.data || [];
    } catch (error) {
      console.error("Error fetching judges by event ID:", error);
      return [];
    }
  }


    async getJudgesByPageantId(pageantId: number): Promise<EventPersonnelModel[]> {
    try {
      const result = await this.EventPersonnelService.getJudgesByPageantDetail(pageantId);
      return result?.data || [];
    } catch (error) {
      console.error("Error fetching judges by pageant ID:", error);
      return [];
    }
  }
  
  async getPersonnelByEventId(eventId: number): Promise<EventPersonnelModel[]> {
    try {
      const result = await this.EventPersonnelService.getPersonnelByEventDetail(eventId);
      return result?.data || [];
    } catch (error) {
      console.error("Error fetching personnel by event ID:", error);
      return [];
    }
  }

async addPersonnelToEvent(eventPersonnel: EventPersonnelModel) {
  try {
    await this.EventPersonnelService.addPersonnelToEventCreate(eventPersonnel);
    await this.getPersonnelByEventId(eventPersonnel.eventId ?? 0); // 👈 Refresh from backend
  } catch (error) {
    console.error("Error adding personnel to event:", error);
  }
}


  async removePersonnelToEvent(eventPersonnelId) {
    try {
      await this.EventPersonnelService.removePersonnelFromEventDelete(eventPersonnelId);
    } catch (error) {
      console.error("Error adding judge to event:", error);
    }
  }

  storeEventsInSessionStorage(events: EventModel[]) {
    const compressedEvents = compressData(events);
    sessionStorage.setItem('upcomingEvents', compressedEvents);
  }

  getEventsFromSessionStorage(): EventModel[] {
    const compressedEvents = sessionStorage.getItem('upcomingEvents');
    return compressedEvents ? decompressData(compressedEvents) : [];
  }

  async getEvent(id: number) {
    try {
      var eventResult = await this.EventService.getEventById(id)
      if (eventResult.data.data) {
        runInAction(() => {
          this.CurrentEvent = eventResult?.data.data!;
        });
      }
    } catch (error) {
      console.log("Error:" + JSON.stringify(error));
    }
  }

  async getEventMetaData(eventId: number) {
    this.loading = true;  // Set loading to true before starting the fetch
    try {
      const response = await this.EventService.getEventMetadata(eventId);
      console.log("event meta data fetch from store:", response.data.data);
      // Optionally, handle any additional response processing here (e.g., checking if it was successful)
  
      return response.data.data;  // Return the fetched response data directly
    } catch (error) {
      console.error("Error fetching event meta data:", error);
      throw error;  // Optionally, you can re-throw the error if needed
    } finally {
      this.loading = false;  // Set loading to false after the request is complete
    }
  }
  
  async getEventMetaDataByPageantId(pageantId: number) {
    this.loading = true;  // Set loading to true before starting the fetch
    try {
      const response = await this.EventService.getEventMetadataByPageantId(pageantId);
      console.log("event meta data fetch from store:", response.data.data);
      // Optionally, handle any additional response processing here (e.g., checking if it was successful)
  
      return response.data.data;  // Return the fetched response data directly
    } catch (error) {
      console.error("Error fetching event meta data:", error);
      throw error;  // Optionally, you can re-throw the error if needed
    } finally {
      this.loading = false;  // Set loading to false after the request is complete
    }
  }

  async getEventPublicDetails(eventId: number, eventName: string) {
    this.loading = true;
    try {
      // Make a fetch call to get event details
      const response = await fetch(`/Events/${eventId}/${eventName}/details`);
      if (response.ok) {
        const data = await response.json();
        this.eventPublicDetails = data;
      } else {
        console.error("Event not found.");
      }
    } catch (error) {
      console.error("Error fetching event details:", error);
    } finally {
      this.loading = false;
    }
  }

  //dynamic fetch  - tesing
    async fetchBasicEvents(
    when: TemporalFilter,
    limit: number,
    opts?: {
      cursor?: Partial<EventCursor>;
      includeMetadata?: boolean;
      signal?: AbortSignal;
    }
  ): Promise<BasicPageantDTOPagedResult> {
    const qs = new URLSearchParams();

    // enum goes as number by default (0,1,2)
    qs.set("when", String(when));
    qs.set("limit", String(limit));

    if (opts?.cursor?.sortDateUtc) qs.set("cursorDateUtc", opts.cursor.sortDateUtc);
    if (opts?.cursor?.sortEventId != null)
      qs.set("cursorEventId", String(opts.cursor.sortEventId));

 

    if (opts?.includeMetadata) qs.set("includeMetadata", "true");

    const res = await fetch(
      // your controller route:
      `/Event/GetBasicEventsDynamic?${qs.toString()}`,
      { signal: opts?.signal }
    );
    if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
    return (await res.json()) as BasicPageantDTOPagedResult;
  }

  // Optionally, clear event details when necessary
  clearEventDetails() {
    this.eventPublicDetails = null;
  }



  async saveEvent(model: EventBaseModel) {
    try {
      var eventResult = await this.EventService.addEvent(model);
      if (eventResult.data.data) {
        runInAction(() => {
          this.CurrentEvent = eventResult?.data.data!;
        });
      }
    } catch (error) {
      console.log("Error: " + JSON.stringify(error));
    }
  }

  async getEventByDirector(id: number) {
    try {
      var eventResult = await this.EventService.getEventByDirectorId(id)
      // console.log("EventResult", eventResult);

      if (eventResult.data.data) {
        runInAction(() => {
          this.DirectorEventList = eventResult.data.data!;
        });
      }
    } catch (error) {
      console.log("Error: " + JSON.stringify(error));
    }
  }

  // async getFutureEvents() {
  //     try {
  //         var eventResult = await this.EventService.getAllFutureEvents()
  //         if (eventResult.data.data) {
  //             runInAction(() => {
  //                 this.FutureEventList = eventResult.data.data!;
  //               });
  //         }
  //     }
  //     catch (error) {
  //         console.log("Error: " + JSON.stringify(error));
  //     }
  // }

  async getFutureEvents(reset: boolean = false) {
    try {
      if (this.FutureEventsListRequest.status == RequestStatus.ACTIVE || this.FutureEventsListRequest.status == RequestStatus.COMPLETED
        && !reset) {
        return;
      }
      this.FutureEventsListRequest = { id: 0, status: RequestStatus.ACTIVE };
      var futureEventsListResult = await this.EventService.getAllFutureEvents()
      // console.log("data", pageantResult);
      if (futureEventsListResult.data.data) {
        this.FutureEventsListRequest.status = RequestStatus.COMPLETED;
        this.FutureEventList = futureEventsListResult?.data.data;
        this.storeEventsInSessionStorage(this.FutureEventList);
      }
    } catch (error) {
      this.FutureEventsListRequest.status = RequestStatus.ERROR;
      console.log("Error:" + JSON.stringify(error));
    }
  }

  async getFutureFeaturedEvents(reset: boolean = false) {
    try {
      if (this.FutureEventsListRequest.status == RequestStatus.ACTIVE || this.FutureEventsListRequest.status == RequestStatus.COMPLETED
        && !reset) {
        return;
      }
      this.FutureEventsListRequest = { id: 0, status: RequestStatus.ACTIVE };
      var futureEventsListResult = await this.EventService.getAllFutureEvents()
      // console.log("data", pageantResult);
      if (futureEventsListResult.data.data) {
        this.FutureEventsListRequest.status = RequestStatus.COMPLETED;
        this.FutureEventList = futureEventsListResult?.data.data;
      }
    } catch (error) {
      this.FutureEventsListRequest.status = RequestStatus.ERROR;
      console.log("Error:" + JSON.stringify(error));
    }
  }

  async getFutureEventsSearchFilter(page: number = 1, pageSize: number = 10, reset: boolean = false): Promise<EventModel[]> {
    try {
      if (this.FutureEventsListRequest.status === RequestStatus.ACTIVE || (this.FutureEventsListRequest.status === RequestStatus.COMPLETED && !reset && page === 1)) {
        return this.FutureEventList;
      }

      if (reset) {
        this.FutureEventList = []; // Clear the current event list if resetting
      }

      this.FutureEventsListRequest = { id: 0, status: RequestStatus.ACTIVE };
      const futureEventsListResult = await this.EventService.getAllFutureEventsSearchFilter({ page, pageSize });

      if (futureEventsListResult.data && futureEventsListResult.data.data) {
        runInAction(() => {
          this.FutureEventsListRequest.status = RequestStatus.COMPLETED;
          if (reset) {
            this.FutureEventList = futureEventsListResult.data.data!;
          } else {
            this.FutureEventList = [
              ...this.FutureEventList,
              ...futureEventsListResult.data.data!,
            ];
          }
          this.storeEventsInSessionStorage(this.FutureEventList);
        });

        return this.FutureEventList;
      }

      return [];
    } catch (error) {
      runInAction(() => {
        this.FutureEventsListRequest.status = RequestStatus.ERROR;
      });
      console.log("Error:" + JSON.stringify(error));
      return [];
    }
  }

  async fetchTotalFutureEventsCount(): Promise<number> {
    try {
      const response = await fetch('/Event/GetTotalFutureEventsCount');
      const totalCount = await response.json();
      runInAction(() => {
        this.totalFutureEventsCount = totalCount;
      });
      return totalCount;
    } catch (error) {
      console.error('Error fetching total events count:', error);
      return 0;
    }
  }





  // async getFutureEventsBasicInfo() {
  //     try {
  //         var eventResult = await this.EventService.getAllFutureEventsBasicInfo()
  //         if (eventResult.data.data) {
  //             runInAction(() => {
  //                 this.FutureEventList = eventResult.data.data!;
  //               });
  //         }
  //     }
  //     catch (error) {
  //         console.log("Error: " + JSON.stringify(error));
  //     }
  // }

  // async getPastEvents() {
  //     try {
  //         var eventResult = await this.EventService.getAllPastEvents()
  //         if (eventResult.data.data) {
  //             runInAction(() => {
  //                 this.PastEventList = eventResult.data.data!;
  //               });
  //         }
  //     }
  //     catch (error) {
  //         console.log("Error: " + JSON.stringify(error));
  //     }
  // }

  async getPastEvents(reset: boolean = false) {
    try {
      if (this.PastEventsListRequest.status == RequestStatus.ACTIVE || this.PastEventsListRequest.status == RequestStatus.COMPLETED
        && !reset) {
        return;
      }
      this.PastEventsListRequest = { id: 0, status: RequestStatus.ACTIVE };
      var pastEventsListResult = await this.EventService.getAllPastEvents()
      // console.log("data", pageantResult);
      if (pastEventsListResult.data.data) {
        this.PastEventsListRequest.status = RequestStatus.COMPLETED;
        this.PastEventList = pastEventsListResult?.data.data;
      }
    } catch (error) {
      this.PastEventsListRequest.status = RequestStatus.ERROR;
      console.log("Error:" + JSON.stringify(error));
    }
  }

  async getAllEvents() {
    try {
      var eventResult = await this.EventService.getAllEventsForAdmin()
      if (eventResult.data.data) {
        runInAction(() => {
          this.FutureEventList = eventResult.data.data!;
        });
      }
    }
    catch (error) {
      console.log("Error: " + JSON.stringify(error));
    }

  }

  async updateDirector(directorId: number, eventId: number) {
    try {
      var eventResult = await this.EventService.assignDirectorUserIdToEventId(eventId, directorId)
      console.log("Updating Director", eventResult, directorId, eventId);
      if (eventResult.data.data) {
        return true;
      }
    }
    catch (error) {
      console.log("Error: " + JSON.stringify(error));
      return false
    }

  }

  async getAllEventsForUser(userId: number): Promise<EventModel[]> {
    try {
        const result = await this.EventPersonnelService.getEventsForJudgeDetail(userId); // Fetch events for the user
        if (result?.data) {
            return result.data; // Assuming this is an array of EventModel
        } else {
            return [];
        }
    } catch (error) {
        console.error("Error fetching events for user:", error);
        return [];
    }
}

async getJudgeEvents(userId: number): Promise<EventModel[]> {
  try {
      const events = await this.EventPersonnelService.getEventsForJudgeDetail(userId);
      return events.data;
  } catch (error) {
      console.error("Error fetching judge events:", error);
      return [];
  }
}

// async getPersonnelEvents(userId: number): Promise<EventModel[]> {
//   try {
//       const events = await this.EventPersonnelService.getEventsForPersonnelDetail(userId);
//       return events.data;
//   } catch (error) {
//       console.error("Error fetching personnel events:", error);
//       return [];
//   }
// }



async getPersonnelEvents(userId: number): Promise<{ upcomingEvents: EventModel[]; pastEvents: EventModel[] }> {
  try {
    // Fetch the API response
    const response = await this.EventPersonnelService.getEventsForPersonnelDetail(userId);

    // Ensure the response is okay
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Parse the JSON body
    const data: PersonnelEventsResponse = await response.json();

    return {
      upcomingEvents: data.upcomingEvents || [],
      pastEvents: data.pastEvents || [],
    };
  } catch (error) {
    console.error("Error fetching personnel events:", error);
    return { upcomingEvents: [], pastEvents: [] };
  }
}

async getDirectorEvents(userId: number): Promise<{ upcomingEvents: EventModel[]; pastEvents: EventModel[] }> {
  try {
    // Fetch the API response
    const response = await this.EventService.getEventsForDirectorDetail(userId);

    // Ensure the response is okay
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Parse the JSON body
    const data: PersonnelEventsResponse = await response.json();

    return {
      upcomingEvents: data.upcomingEvents || [],
      pastEvents: data.pastEvents || [],
    };
  } catch (error) {
    console.error("Error fetching director events:", error);
    return { upcomingEvents: [], pastEvents: [] };
  }
}





async fetchPersonnelTypes(): Promise<void> {
  try {
    const response = await this.EventPersonnelService.getPersonnelTypesList();
    this.personnelTypes = response.data;
  } catch (error) {
    console.error("Error fetching personnel types:", error);
    throw error;
  }
}

  async resetCurrentEvent() {
    this.CurrentEvent = {};
  }
}

let eventStore: EventStore;

export function GetEventStore() {
  if (!eventStore) {
    eventStore = new EventStore({}, [], [], [], [], []);
  }
  return eventStore;
}